class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    console.log('PredictiveSearch constructor called');
    console.log('predictiveSearchResults found:', this.predictiveSearchResults);
    console.log('allPredictiveSearchInstances found:', this.allPredictiveSearchInstances.length);

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    // Ensure the input event listener is set up for onChange
    this.input.addEventListener('input', this.onChange.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    try {
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        // Remove the results when they are no longer relevant for the new search term
        // so they don't show up when the dropdown opens again
        const resultsWrapper = this.querySelector('#predictive-search-results-groups-wrapper');
        if (resultsWrapper) {
          resultsWrapper.remove();
        }
      }
    } catch (error) {
      console.warn('Error removing results wrapper:', error);
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    try {
      const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
      const currentButtonText = searchForTextElement?.innerText;

      if (currentButtonText && previousTerm && newTerm) {
        try {
          const matches = currentButtonText.match(new RegExp(previousTerm, 'g'));
          if (matches && matches.length > 1) {
            // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
            return;
          }
          const newButtonText = currentButtonText.replace(previousTerm, newTerm);
          searchForTextElement.innerText = newButtonText;
        } catch (regexError) {
          console.warn('Error in regex operation:', regexError);
        }
      }
    } catch (error) {
      console.warn('Error in updateSearchForTerm:', error);
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    // Only update status element if it exists
    if (this.statusElement) {
      this.statusElement.textContent = '';
    }

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    console.log('getSearchResults called with:', searchTerm);

    // Check if routes object is available (now checking window.routes)
    if (typeof window.routes === 'undefined' || !window.routes.predictive_search_url) {
      console.error('window.routes object or predictive_search_url not available:', window.routes);
      return;
    }

    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      console.log('Using cached results for:', queryKey);
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    console.log(
      'Fetching search results from:',
      `${window.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`
    );

    fetch(`${window.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        console.log('Received search response, length:', text.length);
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        console.log('Extracted results markup:', resultsMarkup.substring(0, 200) + '...');

        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        console.error('Error fetching search results:', error);
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    // Only set live region text if we have a status element
    if (this.statusElement && this.loadingText) {
      this.setLiveRegionText(this.loadingText);
    }

    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) {
      console.warn('statusElement not found, skipping live region text update');
      return;
    }

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      if (this.statusElement) {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    console.log('renderSearchResults called with markup length:', resultsMarkup.length);
    console.log('predictiveSearchResults element:', this.predictiveSearchResults);

    if (!this.predictiveSearchResults) {
      console.error('predictiveSearchResults element not found, cannot render results');
      return;
    }

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    // Remove the black suggestion box after rendering results
    this.removeBlackSuggestionBox();

    console.log('Results rendered, calling setLiveRegionResults and open');
    this.setLiveRegionResults();
    this.open();
  }

  removeBlackSuggestionBox() {
    // Remove the black suggestion box elements
    const blackBoxSelectors = [
      '.predictive-search__search-for-button',
      '[data-predictive-search-search-for-text]',
      '.predictive-search__item--term',
      '#predictive-search-option-search-keywords',
    ];

    blackBoxSelectors.forEach((selector) => {
      const elements = this.querySelectorAll(selector);
      elements.forEach((element) => {
        console.log('Removing black suggestion box element:', element);
        element.remove();
      });
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    try {
      const liveRegionElement = this.querySelector('[data-predictive-search-live-region-count-value]');
      if (liveRegionElement && liveRegionElement.textContent) {
        this.setLiveRegionText(liveRegionElement.textContent);
      }
    } catch (error) {
      console.warn('Error setting live region results:', error);
    }
  }

  getResultsMaxHeight() {
    try {
      const sectionHeader = document.querySelector('.section-header');
      if (sectionHeader) {
        this.resultsMaxHeight = window.innerHeight - sectionHeader.getBoundingClientRect().bottom;
      } else {
        // Fallback: use a reasonable default height
        this.resultsMaxHeight = Math.min(400, window.innerHeight * 0.6);
      }
      return this.resultsMaxHeight;
    } catch (error) {
      console.warn('Error calculating results max height:', error);
      // Fallback: use a reasonable default height
      this.resultsMaxHeight = Math.min(400, window.innerHeight * 0.6);
      return this.resultsMaxHeight;
    }
  }

  open() {
    console.log('open() called');
    console.log('predictiveSearchResults:', this.predictiveSearchResults);
    console.log('resultsMaxHeight:', this.resultsMaxHeight);

    if (!this.predictiveSearchResults) {
      console.error('predictiveSearchResults element not found, cannot open dropdown');
      return;
    }

    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;

    // Hide the black suggestion box when opening
    this.hideBlackSuggestionBox();

    console.log('Dropdown opened, isOpen:', this.isOpen);
  }

  hideBlackSuggestionBox() {
    // Hide the black suggestion box with CSS
    const blackBoxSelectors = [
      '.predictive-search__search-for-button',
      '[data-predictive-search-search-for-text]',
      '.predictive-search__item--term',
      '#predictive-search-option-search-keywords',
    ];

    blackBoxSelectors.forEach((selector) => {
      const elements = this.querySelectorAll(selector);
      elements.forEach((element) => {
        console.log('Hiding black suggestion box element:', element);
        element.style.display = 'none';
        element.style.visibility = 'hidden';
        element.style.opacity = '0';
        element.style.height = '0';
        element.style.width = '0';
        element.style.overflow = 'hidden';
        element.style.position = 'absolute';
        element.style.left = '-9999px';
      });
    });
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute('style');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
